/**
 * Group management – create groups, approve members. Groups can be apartment (requires flat numbers), club, or custom.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileLink from '../components/ProfileLink';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { FEATURES } from '../constants/plans';
import { collection, getDocs, doc, getDoc, addDoc, setDoc, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { groupCreate, groupUpdate, groupApproveMember, groupRevokeMember, groupAddMember } from '../api/groupApi';
import { saveUserToAlgolia, findUserByEmailOrPhone } from '../services/userAlgoliaService';
import { GROUP_TYPES, GROUP_TYPE_LABELS, groupTypeRequiresMemberId, DEFAULT_GROUP_IS_OPEN } from '../constants/groups';
import { useGame } from '../contexts/GameContext';
import { useAlert } from '../contexts/AlertContext';
import { expandRangeExpression } from '../utils/flatNumberRanges';
import { isSuperAdmin, SUPER_ADMIN_EMAIL } from '../constants/roles';
import EntityProfileFields, { EntityProfileDisplay } from '../components/EntityProfileFields';
import './GroupManage.css';

export default function GroupManage() {
  const { currentUser, userProfile } = useAuth();
  const { hasFeature } = usePlan();
  const { games: gamesList } = useGame();
  const { showAlert, showToast } = useAlert();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myGroupIds, setMyGroupIds] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [groupType, setGroupType] = useState(GROUP_TYPES.CUSTOM);
  const [isOpen, setIsOpen] = useState(DEFAULT_GROUP_IS_OPEN);
  const [groupGameIds, setGroupGameIds] = useState([]);
  const [flatNumbersInput, setFlatNumbersInput] = useState('');
  const [rangeExpression, setRangeExpression] = useState('');
  const [groupProfileFields, setGroupProfileFields] = useState({ avatarURL: '', description: '', phone: '', address: '', mapUrl: '' });
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingIsOpen, setEditingIsOpen] = useState(true);
  const [editingGameIds, setEditingGameIds] = useState([]);
  const [editingInvitedEmails, setEditingInvitedEmails] = useState([]);
  const [editingInviteEmailInput, setEditingInviteEmailInput] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [addMemberGroupId, setAddMemberGroupId] = useState(null);
  const [addMemberQuery, setAddMemberQuery] = useState('');
  const [addMemberResults, setAddMemberResults] = useState([]);
  const [addMemberSearching, setAddMemberSearching] = useState(false);
  const [addTeamMemberTeamId, setAddTeamMemberTeamId] = useState(null);
  const [addTeamMemberQuery, setAddTeamMemberQuery] = useState('');
  const [addTeamMemberResults, setAddTeamMemberResults] = useState([]);
  const [addTeamMemberSearching, setAddTeamMemberSearching] = useState(false);

  const canManageAll = isSuperAdmin(userProfile);
  const canEditGroup = (group) => canManageAll || (group && (group.adminIds || []).includes(currentUser?.uid));
  const canManageTeam = (team) => team && (canManageAll || (team.adminIds || []).includes(currentUser?.uid) || (groups.find(g => g.id === team.groupId) && (groups.find(g => g.id === team.groupId).adminIds || []).includes(currentUser?.uid)));
  const canCreate = !!currentUser?.uid && hasFeature(FEATURES.GROUP_MANAGE);
  const superAdminUidCached = useRef(null);

  /** Resolve system (super) admin UID so they are default admin for all groups. */
  const getSuperAdminUid = useCallback(async () => {
    if (superAdminUidCached.current !== null) return superAdminUidCached.current;
    try {
      let snap = await getDocs(query(collection(db, 'users'), where('isAdmin', '==', 'super'), limit(1)));
      if (snap.empty) {
        snap = await getDocs(query(collection(db, 'users'), where('email', '==', SUPER_ADMIN_EMAIL), limit(1)));
      }
      const uid = snap.empty ? null : snap.docs[0].id;
      superAdminUidCached.current = uid;
      return uid;
    } catch {
      return null;
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'groups'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setGroups(list);
      const adminIds = list.filter(g => (g.adminIds || []).includes(currentUser.uid)).map(g => g.id);
      setMyGroupIds(adminIds);
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const [groupMembers, setGroupMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showCreateTeamGroupId, setShowCreateTeamGroupId] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamLogo, setNewTeamLogo] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'users'));
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchGroupMembers = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'groupMembers'));
      setGroupMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching group members:', err);
    }
  }, []);

  const fetchTeams = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'teams'));
      setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'teamMembers'));
      setTeamMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    fetchGroups();
    fetchUsers();
    fetchGroupMembers();
    fetchTeams();
    fetchTeamMembers();
  }, [currentUser, fetchGroups, fetchUsers, fetchGroupMembers, fetchTeams, fetchTeamMembers]);

  function getFlatList() {
    return flatNumbersInput.split(',').map(s => s.trim()).filter(Boolean);
  }

  function handleAddFromRange() {
    const expanded = expandRangeExpression(rangeExpression);
    const current = getFlatList();
    const combined = [...current];
    expanded.forEach(v => { if (!combined.includes(v)) combined.push(v); });
    setFlatNumbersInput(combined.join(', '));
    if (expanded.length) setRangeExpression('');
  }

  async function handleCreateGroup(e) {
    e.preventDefault();
    setSaving(true);
    const groupName = (name || '').trim();
    if (!groupName) {
      showAlert('Enter group name.', { variant: 'warning' });
      setSaving(false);
      return;
    }
    if (groupTypeRequiresMemberId(groupType)) {
      const flats = getFlatList();
      if (flats.length === 0) {
        showAlert('Apartment type requires at least one flat number.', { variant: 'warning' });
        setSaving(false);
        return;
      }
    }
    try {
      const superAdminUid = await getSuperAdminUid();
      const adminIds = [currentUser.uid, ...(superAdminUid ? [superAdminUid] : [])].filter((id, i, arr) => arr.indexOf(id) === i);
      const payload = {
        name: groupName,
        shortCode: (shortCode || '').trim(),
        type: groupType,
        isOpen: !!isOpen,
        gameIds: Array.isArray(groupGameIds) ? groupGameIds.filter(Boolean) : [],
        adminIds,
        createdBy: currentUser.uid,
        invitedEmails: [],
        avatarURL: (groupProfileFields.avatarURL || '').trim() || null,
        description: (groupProfileFields.description || '').trim() || null,
        phone: (groupProfileFields.phone || '').trim() || null,
        address: (groupProfileFields.address || '').trim() || null,
        mapUrl: (groupProfileFields.mapUrl || '').trim() || null
      };
      if (groupTypeRequiresMemberId(groupType)) {
        payload.flatNumbers = getFlatList();
      }
      await groupCreate(payload);
      showToast('Group created. You are the admin.', { variant: 'success' });
      setShowCreate(false);
      setName('');
      setShortCode('');
      setIsOpen(DEFAULT_GROUP_IS_OPEN);
      setGroupGameIds([]);
      setFlatNumbersInput('');
      setRangeExpression('');
      setGroupProfileFields({ avatarURL: '', description: '', phone: '', address: '', mapUrl: '' });
      fetchGroups();
    } catch (err) {
      showAlert('Failed to create group: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function startEditGroup(group) {
    setEditingGroupId(group.id);
    setEditingIsOpen(!!group.isOpen);
    setEditingGameIds(Array.isArray(group.gameIds) ? [...group.gameIds] : []);
    setEditingInvitedEmails(Array.isArray(group.invitedEmails) ? [...group.invitedEmails] : []);
    setEditingInviteEmailInput('');
  }

  function cancelEditGroup() {
    setEditingGroupId(null);
    setEditingIsOpen(true);
    setEditingGameIds([]);
    setEditingInvitedEmails([]);
    setEditingInviteEmailInput('');
  }

  function addInvitedEmail() {
    const email = (editingInviteEmailInput || '').trim().toLowerCase();
    if (!email) return;
    if (editingInvitedEmails.includes(email)) return;
    setEditingInvitedEmails([...editingInvitedEmails, email]);
    setEditingInviteEmailInput('');
  }

  function removeInvitedEmail(email) {
    setEditingInvitedEmails(editingInvitedEmails.filter(e => e !== email));
  }

  async function saveGroupSettings(groupId) {
    const group = groups.find(g => g.id === groupId);
    if (!group || !canEditGroup(group)) return;
    setSaving(true);
    try {
      const invitedEmails = (Array.isArray(editingInvitedEmails) ? editingInvitedEmails : [])
        .map(e => (e || '').trim().toLowerCase())
        .filter(Boolean);
      await groupUpdate(groupId, {
        isOpen: !!editingIsOpen,
        gameIds: Array.isArray(editingGameIds) ? editingGameIds.filter(Boolean) : [],
        invitedEmails
      });
      showToast('Group settings saved.', { variant: 'success' });
      cancelEditGroup();
      fetchGroups();
    } catch (err) {
      showAlert('Failed to save group settings: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function toggleApproved(membershipId, userId, currentApproved, groupId, group) {
    try {
      if (currentApproved) {
        await groupRevokeMember(groupId, userId);
      } else {
        await groupApproveMember(groupId, userId);
      }
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      const membersSnap = await getDocs(query(collection(db, 'groupMembers'), where('userId', '==', userId)));
      const approvedGroupIds = membersSnap.docs.filter((d) => d.data().approved === true).map((d) => d.data().groupId);
      const userForAlgolia = { id: userId, displayName: userData.displayName, email: userData.email, phone: userData.phone };
      await saveUserToAlgolia(userForAlgolia, approvedGroupIds);
      fetchGroupMembers();
      fetchUsers();
    } catch (err) {
      const msg = err?.message || String(err);
      const isPermission = err?.code === 'permission-denied' || /permission|insufficient/i.test(msg);
      showAlert(isPermission
        ? "Couldn't update approval: you may not have permission to update this member. Ensure you're listed as an admin for this group, then try again. If it persists, contact a system admin."
        : 'Error: ' + msg, { variant: 'error' });
    }
  }

  const groupsForAdmin = canManageAll ? groups : groups.filter(g => myGroupIds.includes(g.id));
  const groupSearchLower = (groupSearch || '').trim().toLowerCase();
  const groupsToShow = groupSearchLower
    ? groupsForAdmin.filter(g => {
        const name = (g.name || '').toLowerCase();
        const code = (g.shortCode || '').toLowerCase();
        return name.includes(groupSearchLower) || code.includes(groupSearchLower);
      })
    : groupsForAdmin;

  const membersByGroup = {};
  const usersById = Object.fromEntries(users.map(u => [u.id, u]));
  groupsToShow.forEach(g => {
    const members = groupMembers.filter(m => m.groupId === g.id);
    membersByGroup[g.id] = members.map(m => ({
      ...m,
      displayName: usersById[m.userId]?.displayName || usersById[m.userId]?.email || '—',
      email: usersById[m.userId]?.email || '—',
      phone: usersById[m.userId]?.phone || '—'
    }));
  });

  async function handleSearchAddMember(groupId) {
    const q = (addMemberQuery || '').trim();
    if (!q) return;
    setAddMemberGroupId(groupId);
    setAddMemberSearching(true);
    setAddMemberResults([]);
    try {
      let hits = await findUserByEmailOrPhone(q, { hitsPerPage: 10 });
      if (hits.length === 0) {
        const emailMatch = users.find(u => (u.email || '').trim().toLowerCase() === q.toLowerCase());
        const phoneMatch = users.find(u => (u.phone || '').toString().trim() === q || (u.phone || '').toString().replace(/\s/g, '') === q.replace(/\s/g, ''));
        if (emailMatch) hits = [{ objectID: emailMatch.id, id: emailMatch.id, name: emailMatch.displayName || emailMatch.email || '', email: emailMatch.email || '', phone: emailMatch.phone || '' }];
        else if (phoneMatch) hits = [{ objectID: phoneMatch.id, id: phoneMatch.id, name: phoneMatch.displayName || phoneMatch.email || '', email: phoneMatch.email || '', phone: phoneMatch.phone || '' }];
      }
      setAddMemberResults(hits);
    } catch (err) {
      showAlert('Search failed: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setAddMemberSearching(false);
    }
  }

  function membershipId(userId, groupId) {
    return `${userId}_${groupId}`;
  }

  async function handleAddMemberToGroup(userId, groupId, group) {
    if (!currentUser?.uid || !groupId || !userId) return;
    const mid = membershipId(userId, groupId);
    const existing = groupMembers.find(m => m.id === mid);
    if (existing) {
      showAlert('This user is already in the group.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await groupAddMember(groupId, userId, {});
      const userSnap = await getDoc(doc(db, 'users', userId));
      const userData = userSnap.exists() ? userSnap.data() : {};
      const membersSnap = await getDocs(query(collection(db, 'groupMembers'), where('userId', '==', userId)));
      const approvedGroupIds = membersSnap.docs.filter((d) => d.data().approved === true).map((d) => d.data().groupId);
      await saveUserToAlgolia(
        { id: userId, displayName: userData.displayName, email: userData.email, phone: userData.phone },
        approvedGroupIds
      );
      showToast('Member added and approved.', { variant: 'success' });
      setAddMemberGroupId(null);
      setAddMemberQuery('');
      setAddMemberResults([]);
      fetchGroupMembers();
      fetchGroups();
    } catch (err) {
      showAlert('Failed to add member: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function teamMemberId(userId, teamId) {
    return `${userId}_${teamId}`;
  }

  async function handleCreateTeam(groupId) {
    const groupName = (newTeamName || '').trim();
    if (!groupId || !groupName) {
      showAlert('Enter team name.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'teams'), {
        groupId,
        name: groupName,
        avatarURL: (newTeamLogo || '').trim() || null,
        adminIds: [currentUser.uid],
        isOpen: false,
        invitedEmails: [],
        createdAt: new Date(),
        createdBy: currentUser.uid
      });
      showToast('Team created.', { variant: 'success' });
      setShowCreateTeamGroupId(null);
      setNewTeamName('');
      setNewTeamLogo('');
      fetchTeams();
    } catch (err) {
      showAlert('Failed to create team: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleSearchAddTeamMember(teamId) {
    const q = (addTeamMemberQuery || '').trim();
    if (!q) return;
    setAddTeamMemberTeamId(teamId);
    setAddTeamMemberSearching(true);
    setAddTeamMemberResults([]);
    try {
      let hits = await findUserByEmailOrPhone(q, { hitsPerPage: 10 });
      if (hits.length === 0) {
        const emailMatch = users.find(u => (u.email || '').trim().toLowerCase() === q.toLowerCase());
        const phoneMatch = users.find(u => (u.phone || '').toString().trim() === q || (u.phone || '').toString().replace(/\s/g, '') === q.replace(/\s/g, ''));
        if (emailMatch) hits = [{ objectID: emailMatch.id, id: emailMatch.id, name: emailMatch.displayName || emailMatch.email || '', email: emailMatch.email || '', phone: emailMatch.phone || '' }];
        else if (phoneMatch) hits = [{ objectID: phoneMatch.id, id: phoneMatch.id, name: phoneMatch.displayName || phoneMatch.email || '', email: phoneMatch.email || '', phone: phoneMatch.phone || '' }];
      }
      setAddTeamMemberResults(hits);
    } catch (err) {
      showAlert('Search failed: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setAddTeamMemberSearching(false);
    }
  }

  async function handleAddMemberToTeam(userId, teamId) {
    if (!currentUser?.uid || !teamId || !userId) return;
    const mid = teamMemberId(userId, teamId);
    const existing = teamMembers.find(m => m.id === mid);
    if (existing) {
      showAlert('This user is already on the team.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await setDoc(doc(db, 'teamMembers', mid), {
        userId,
        teamId,
        role: 'member',
        approved: true,
        approvedBy: currentUser.uid,
        approvedAt: new Date(),
        joinedAt: new Date()
      });
      showToast('Team member added.', { variant: 'success' });
      setAddTeamMemberTeamId(null);
      setAddTeamMemberQuery('');
      setAddTeamMemberResults([]);
      fetchTeamMembers();
    } catch (err) {
      showAlert('Failed to add team member: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="group-manage-container">
      <div className="group-manage-header">
        <h1>Groups</h1>
        <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666', maxWidth: '560px' }}>
          Create and manage your groups. The same group is used across sports. Groups are the source for who can see and register for tournaments—create a group, then use it when creating a tournament.
        </p>
        <div className="group-manage-actions">
          <button type="button" onClick={() => navigate('/dashboard')} className="btn-secondary">Back to Dashboard</button>
          {canCreate && (
            <button type="button" onClick={() => setShowCreate(!showCreate)} className="btn-primary">
              {showCreate ? 'Cancel' : 'Create group'}
            </button>
          )}
        </div>
      </div>

      {!hasFeature(FEATURES.GROUP_MANAGE) && (
        <div className="card-ui" style={{ padding: '16px', marginBottom: '16px', background: 'var(--muted, #f5f5f5)', borderRadius: '12px' }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
            <strong>Create & manage groups</strong> is a Premium feature. Upgrade to create new groups and manage teams.
          </p>
          <button type="button" onClick={() => navigate('/subscription')} className="btn-primary">
            View plans & upgrade
          </button>
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreateGroup} className="group-create-form card-ui">
          <h2>Create group</h2>
          <div className="form-group">
            <label>Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Tennis Club A" required />
          </div>
          <div className="form-group">
            <label>Type</label>
            <select value={groupType} onChange={(e) => setGroupType(e.target.value)}>
              {Object.entries(GROUP_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <small>{groupType === GROUP_TYPES.APARTMENT ? 'Apartment requires flat numbers (member IDs).' : 'Club/Custom do not require member IDs.'}</small>
          </div>
          <div className="form-group">
            <label>Short code (optional)</label>
            <input type="text" value={shortCode} onChange={(e) => setShortCode(e.target.value)} placeholder="e.g. TCA" />
          </div>
          <div className="form-group">
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={isOpen} onChange={(e) => setIsOpen(e.target.checked)} />
              Open group (members join without approval)
            </label>
          </div>
          <div className="form-group">
            <label>Sports (optional)</label>
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666' }}>
              Same group is used across sports. Select which sports this group is for; leave all unchecked for all sports. Used when creating tournaments and for registrations.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
              {(gamesList || []).slice(0, 12).map((g) => (
                <label key={g.id} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="checkbox"
                    checked={(groupGameIds || []).includes(g.id)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...(groupGameIds || []), g.id]
                        : (groupGameIds || []).filter((id) => id !== g.id);
                      setGroupGameIds(next);
                    }}
                  />
                  <span>{g.icon} {g.name}</span>
                </label>
              ))}
            </div>
          </div>
          {groupTypeRequiresMemberId(groupType) && (
            <>
              <div className="form-group">
                <label>Flat numbers * (comma-separated or range)</label>
                <input type="text" value={flatNumbersInput} onChange={(e) => setFlatNumbersInput(e.target.value)} placeholder="101, 102, 103 or 101-105" />
              </div>
              <div className="form-group form-row">
                <input type="text" value={rangeExpression} onChange={(e) => setRangeExpression(e.target.value)} placeholder="e.g. 101-105" />
                <button type="button" onClick={handleAddFromRange} className="btn-secondary">Add from range</button>
              </div>
            </>
          )}
          <div className="form-group">
            <label>Profile &amp; contact (optional)</label>
            <EntityProfileFields
              value={groupProfileFields}
              onChange={(key, val) => setGroupProfileFields((p) => ({ ...p, [key]: val }))}
              avatarAlt="Group"
            />
          </div>
          <div className="form-actions">
            <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Creating…' : 'Create'}</button>
            <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      )}

      <div className="group-manage-content">
        <p className="group-manage-intro">
          Approve or revoke members for your groups. Approved members can see and join group-specific tournaments. Add members by email or phone below.
        </p>
        {groupsForAdmin.length > 0 && (
          <div className="group-search-wrap" style={{ marginBottom: 20 }}>
            <label htmlFor="group-search-input" className="group-search-label">Search groups</label>
            <input
              id="group-search-input"
              type="text"
              className="group-search-input"
              placeholder="Search by group name or short code..."
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              style={{ maxWidth: 320, padding: '10px 12px' }}
            />
          </div>
        )}
        {groupsToShow.length === 0 ? (
          <p className="group-empty">
            {groupsForAdmin.length === 0
              ? 'No groups yet. Create a group above, or ask an admin to add you as a group admin.'
              : 'No groups match your search. Try a different term.'}
          </p>
        ) : (
          groupsToShow.map(group => (
            <div key={group.id} className="group-card card-ui">
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <h3>{group.name || group.id} {group.shortCode && <span className="group-short">({group.shortCode})</span>} {group.isOpen ? <span className="group-open-badge">Public</span> : <span className="group-private-badge">Private</span>}</h3>
                {canEditGroup(group) && editingGroupId !== group.id && (
                  <button type="button" onClick={() => startEditGroup(group)} className="btn-secondary btn-sm">Edit group</button>
                )}
              </div>
              <p className="group-type">Type: {GROUP_TYPE_LABELS[group.type] || group.type}</p>
              <EntityProfileDisplay entity={group} />

              {editingGroupId === group.id && (
                <div className="group-edit-form card-ui" style={{ marginTop: 16, padding: 16, background: 'var(--muted)' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem' }}>Group settings</h4>
                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={editingIsOpen} onChange={(e) => setEditingIsOpen(e.target.checked)} />
                      <span>Public group – anyone can request to join</span>
                    </label>
                    <small style={{ display: 'block', marginTop: 4, color: 'var(--muted-foreground)' }}>Uncheck for private (invite-only): only invited emails can see and request to join.</small>
                  </div>
                  <div className="form-group">
                    <label>Sports (optional)</label>
                    <p style={{ margin: '0 0 8px 0', fontSize: 13, color: 'var(--muted-foreground)' }}>Which sports this group is for; leave all unchecked for all sports.</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                      {(gamesList || []).slice(0, 12).map((g) => (
                        <label key={g.id} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="checkbox"
                            checked={(editingGameIds || []).includes(g.id)}
                            onChange={(e) => {
                              if (e.target.checked) setEditingGameIds([...(editingGameIds || []), g.id]);
                              else setEditingGameIds((editingGameIds || []).filter(id => id !== g.id));
                            }}
                          />
                          <span>{g.icon} {g.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {!editingIsOpen && (
                    <div className="form-group">
                      <label>Invited emails (private group)</label>
                      <p style={{ margin: '0 0 8px 0', fontSize: 13, color: 'var(--muted-foreground)' }}>Only these emails can see and request to join this group. Add one email per line or use the input below.</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <input
                          type="email"
                          value={editingInviteEmailInput}
                          onChange={(e) => setEditingInviteEmailInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInvitedEmail())}
                          placeholder="email@example.com"
                          style={{ padding: '8px 12px', maxWidth: 260 }}
                        />
                        <button type="button" onClick={addInvitedEmail} className="btn-secondary btn-sm">Add</button>
                      </div>
                      {(editingInvitedEmails || []).length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {(editingInvitedEmails || []).map((email) => (
                            <li key={email} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <span>{email}</span>
                              <button type="button" onClick={() => removeInvitedEmail(email)} className="btn-destructive btn-sm">Remove</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <div className="form-actions" style={{ marginTop: 16 }}>
                    <button type="button" onClick={() => saveGroupSettings(group.id)} disabled={saving} className="btn-primary">Save</button>
                    <button type="button" onClick={cancelEditGroup} className="btn-secondary">Cancel</button>
                  </div>
                </div>
              )}

              {canEditGroup(group) && (
                <div className="group-add-member-wrap card-ui" style={{ marginTop: 16, padding: 16, background: 'var(--muted)' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem' }}>Add member by email or phone</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <input
                      type="text"
                      placeholder="Email or phone number"
                      value={addMemberGroupId === group.id ? addMemberQuery : ''}
                      onChange={(e) => { setAddMemberQuery(e.target.value); if (addMemberGroupId !== group.id) setAddMemberGroupId(group.id); setAddMemberResults([]); }}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearchAddMember(group.id))}
                      style={{ padding: '8px 12px', minWidth: 200, maxWidth: 280 }}
                    />
                    <button type="button" onClick={() => handleSearchAddMember(group.id)} disabled={addMemberSearching || !addMemberQuery.trim()} className="btn-secondary btn-sm">
                      {addMemberSearching ? 'Searching…' : 'Search'}
                    </button>
                    {addMemberGroupId === group.id && (addMemberResults.length > 0 || (addMemberQuery.trim() && !addMemberSearching)) && (
                      <button type="button" onClick={() => { setAddMemberGroupId(null); setAddMemberQuery(''); setAddMemberResults([]); }} className="btn-secondary btn-sm">Clear</button>
                    )}
                  </div>
                  {addMemberGroupId === group.id && addMemberResults.length > 0 && (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {addMemberResults.map((u) => {
                        const alreadyIn = (membersByGroup[group.id] || []).some(m => m.userId === (u.objectID || u.id));
                        return (
                          <li key={u.objectID || u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span>{u.name || '—'}</span>
                            {u.email && <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>{u.email}</span>}
                            {u.phone && u.phone !== u.email && <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>{u.phone}</span>}
                            {alreadyIn ? (
                              <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Already in group</span>
                            ) : (
                              <button type="button" onClick={() => handleAddMemberToGroup(u.objectID || u.id, group.id, group)} disabled={saving} className="btn-primary btn-sm">Add</button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {addMemberGroupId === group.id && addMemberQuery.trim() && !addMemberSearching && addMemberResults.length === 0 && (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-foreground)' }}>No user found with that email or phone. They may need to sign up first.</p>
                  )}
                </div>
              )}

              <h4 style={{ margin: '16px 0 8px 0', fontSize: '1rem' }}>Group members</h4>
              <div className="table-container" style={{ marginTop: 0 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>{group.type === GROUP_TYPES.APARTMENT ? 'Flat' : 'Member ID'}</th>
                      <th>Approved</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(membersByGroup[group.id] || []).length === 0 ? (
                      <tr><td colSpan={6}>No members yet. Add by email/phone above or users join from My groups.</td></tr>
                    ) : (
                      (membersByGroup[group.id] || []).map(member => {
                        const approved = member.approved === true;
                        const memberIdVal = member.memberId || '—';
                        return (
                          <tr key={member.id}>
                            <td>
                              {member.userId ? (
                                <ProfileLink userId={member.userId} className="group-member-profile-link">{member.displayName || '—'}</ProfileLink>
                              ) : (
                                (member.displayName || '—')
                              )}
                            </td>
                            <td>{member.email || '—'}</td>
                            <td>{member.phone || '—'}</td>
                            <td>{memberIdVal}</td>
                            <td>{approved ? '✓' : '—'}</td>
                            <td>
                              <button type="button" onClick={() => toggleApproved(member.id, member.userId, approved, group.id, group)} className={`btn-small ${approved ? 'btn-warning' : 'btn-success'}`}>
                                {approved ? 'Revoke' : 'Approve'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {canEditGroup(group) && (
                <>
                  <h4 style={{ margin: '24px 0 12px 0', fontSize: '1rem' }}>Teams</h4>
                  <p style={{ margin: '0 0 12px 0', fontSize: 13, color: 'var(--muted-foreground)' }}>Teams belong to this group. Group and team admins can manage team logo, members, and roles. Team membership rules (visibility, join, exit) work like groups.</p>
                  {showCreateTeamGroupId === group.id ? (
                    <div className="group-edit-form card-ui" style={{ marginTop: 12, padding: 16, background: 'var(--muted)' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem' }}>Create team</h4>
                      <div className="form-group">
                        <label>Team name *</label>
                        <input type="text" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="e.g. A Team" style={{ padding: '8px 12px', maxWidth: 280 }} />
                      </div>
                      <div className="form-group">
                        <label>Team logo URL (optional)</label>
                        <input type="url" value={newTeamLogo} onChange={(e) => setNewTeamLogo(e.target.value)} placeholder="https://..." style={{ padding: '8px 12px', maxWidth: 400 }} />
                      </div>
                      <div className="form-actions" style={{ marginTop: 12 }}>
                        <button type="button" onClick={() => handleCreateTeam(group.id)} disabled={saving || !newTeamName.trim()} className="btn-primary">Create</button>
                        <button type="button" onClick={() => { setShowCreateTeamGroupId(null); setNewTeamName(''); setNewTeamLogo(''); }} className="btn-secondary">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setShowCreateTeamGroupId(group.id)} className="btn-secondary btn-sm" style={{ marginBottom: 12 }}>+ Create team</button>
                  )}
                  {(teams.filter(t => t.groupId === group.id) || []).map(team => {
                    const teamMemberList = (teamMembers.filter(m => m.teamId === team.id) || []).map(m => ({ ...m, displayName: usersById[m.userId]?.displayName || usersById[m.userId]?.email || '—', email: usersById[m.userId]?.email || '—', phone: usersById[m.userId]?.phone || '—' }));
                    return (
                      <div key={team.id} className="group-card card-ui" style={{ marginTop: 12, padding: 16, background: 'var(--muted)', border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                          {team.avatarURL && <img src={team.avatarURL} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />}
                          <h4 style={{ margin: 0, fontSize: '1rem' }}>{team.name || team.id}</h4>
                        </div>
                        {canManageTeam(team) && (
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 13, fontWeight: 500 }}>Add team member by email or phone</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 6 }}>
                              <input
                                type="text"
                                placeholder="Email or phone"
                                value={addTeamMemberTeamId === team.id ? addTeamMemberQuery : ''}
                                onChange={(e) => { setAddTeamMemberQuery(e.target.value); if (addTeamMemberTeamId !== team.id) setAddTeamMemberTeamId(team.id); setAddTeamMemberResults([]); }}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearchAddTeamMember(team.id))}
                                style={{ padding: '6px 10px', minWidth: 160, maxWidth: 240, fontSize: 14 }}
                              />
                              <button type="button" onClick={() => handleSearchAddTeamMember(team.id)} disabled={addTeamMemberSearching || !addTeamMemberQuery.trim()} className="btn-secondary btn-sm">Search</button>
                              {addTeamMemberTeamId === team.id && addTeamMemberResults.length > 0 && (
                                <button type="button" onClick={() => { setAddTeamMemberTeamId(null); setAddTeamMemberQuery(''); setAddTeamMemberResults([]); }} className="btn-secondary btn-sm">Clear</button>
                              )}
                            </div>
                            {addTeamMemberTeamId === team.id && addTeamMemberResults.length > 0 && (
                              <ul style={{ listStyle: 'none', margin: '8px 0 0 0', padding: 0 }}>
                                {addTeamMemberResults.map((u) => {
                                  const alreadyOnTeam = teamMemberList.some(m => m.userId === (u.objectID || u.id));
                                  return (
                                    <li key={u.objectID || u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                      <span>{u.name || '—'}</span>
                                      {u.email && <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{u.email}</span>}
                                      {alreadyOnTeam ? <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Already on team</span> : <button type="button" onClick={() => handleAddMemberToTeam(u.objectID || u.id, team.id)} disabled={saving} className="btn-primary btn-sm">Add</button>}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        )}
                        <div className="table-container">
                          <table className="admin-table" style={{ fontSize: 14 }}>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                              </tr>
                            </thead>
                            <tbody>
                              {teamMemberList.length === 0 ? (
                                <tr><td colSpan={3}>No team members yet.</td></tr>
                              ) : (
                                teamMemberList.map(m => (
                                  <tr key={m.id}>
                                    <td>
                                      {m.userId ? (
                                        <ProfileLink userId={m.userId} className="group-member-profile-link">{m.displayName || '—'}</ProfileLink>
                                      ) : (
                                        (m.displayName || '—')
                                      )}
                                    </td>
                                    <td>{m.email || '—'}</td>
                                    <td>{(m.role || 'member')}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
